// Session Editor metrics table (design §7 point 6, §7a UI, §7c peaks; decisions 2026-10-29).
// Rows are players with an include tick (own periods), the validated metrics, optional zones and peaks, flags and
// hints. Imported periods show Sonra's numbers by default ("calc vs Sonra" switch), with a warning when ours differs
// by more than 2 %. Part B (2026-10-29): acc/dec ≥ 3 m/s², HMLD and explosive distance are our own estimates, shown as
// normal values on own periods and as "ours …" next to Sonra's on imported periods (never one in place of the other,
// never mixed in a squad row); the note under the table says so and holds the reliability notes.
// A player without GPS that day ("no GPS") is neutral: no warning, no numbers, never ranked, not in squad rows.
import React, { useState } from 'react';
import { COLUMNS, ZONE_COLUMNS, ESTIMATE_COLUMNS, cellValues, fmtNum, clock, toMs } from './seShared';

const PEAKS = [['peak60', '1′'], ['peak180', '3′'], ['peak300', '5′'], ['peakHsr60', 'HSR 1′']];
const SOURCE_TITLE = { calc: 'our calculation', sonra: "Sonra's value", estimate: "our estimate: can differ from Sonra's (see the note under the table)" };
const minutesOf = row => { const m = row.metrics || row.calc || row.sonra || {}; return Number.isFinite(m.minutes) ? m.minutes : null; };

function Cell({ col, row, imported, view }) {
  const vals = cellValues(col, row, { imported, view });
  if (!vals.length) return <td title={col.sonraOnly && !imported ? 'not calculated for own periods' : undefined}>–</td>;
  const short = col.count && minutesOf(row) != null && minutesOf(row) < 10 ? ' · short drill (< 10 min): read as ±1' : '';
  return (
    <td>
      {vals.map((v, i) => (v.hidden
        ? <span key="hidden" className={i ? 'se-ours' : undefined} title={`not shown: ${v.hidden}`}>{i ? 'ours –' : '–'}</span>
        : (
          <span key={v.source} className={i && v.source === 'estimate' ? 'se-ours' : v.source === 'estimate' ? 'se-est' : undefined} title={SOURCE_TITLE[v.source] + (v.source === 'estimate' ? short : '')}>
            {i && v.source === 'estimate' ? 'ours ' : ''}{fmtNum(v.value, col.digits)}
          </span>
        )))}
    </td>
  );
}

const sortValue = (col, row, imported, view) => { const v = cellValues(col, row, { imported, view })[0]; return v ? v.value : null; };
const hasData = row => !(row.error || (row.flags && (row.flags.noGps || row.flags.samples === 0 || row.flags.calcError)));

export default function MetricsTable({
  imported, view, rows, noGpsLineup, squad, draftPlayers, readOnly, labelOf, labelOfPlayer, selectedSp, onSelect,
  hintsBySp, pendingBySp, onAccept, onDismiss, canCount = false, onToggleInclude, onSetGroup, onToggleModified, onFollow, showZones, showPeaks, peakMark, onPeak, periodWindow,
}) {
  const [sort, setSort] = useState(null);   // { id, dir }
  const cols = [...COLUMNS, ...(showZones ? ZONE_COLUMNS : []), ...ESTIMATE_COLUMNS];
  const sorted = [...rows].sort((a, b) => {
    // players without data never take part in a ranking: always at the bottom, in name order
    if (hasData(a) !== hasData(b)) return hasData(a) ? -1 : 1;
    if (sort && hasData(a)) {
      const col = cols.find(c => c.id === sort.id) || null;
      const va = col ? sortValue(col, a, imported, view) : null; const vb = col ? sortValue(col, b, imported, view) : null;
      if (va != null || vb != null) return va == null ? 1 : vb == null ? -1 : (vb - va) * sort.dir;
    }
    return labelOf(a.sessionPlayerId).localeCompare(labelOf(b.sessionPlayerId));
  });
  const head = (id, label, title, cls) => (
    <th key={id} className={cls} title={title} onClick={() => setSort(s => (s && s.id === id ? (s.dir === 1 ? { id, dir: -1 } : null) : { id, dir: 1 }))}>
      {label}{sort && sort.id === id ? (sort.dir === 1 ? ' ↓' : ' ↑') : ''}
    </th>
  );
  // our rows take the estimate columns from the estimates' own squad numbers (estValues); Sonra's row from Sonra's
  const squadRow = (label, values, source, estValues) => (
    <tr className="se-squad" key={label}>
      <td className="l" colSpan={imported ? 2 : 5}>{label}</td>
      {cols.map(c => {
        const v = c.sonraOnly && estValues ? estValues[c.estimate] : values ? values[c.id] : null;
        return <td key={c.id} title={source}>{v == null ? '–' : fmtNum(c.scale ? v * c.scale : v, c.digits)}</td>;
      })}
      {showPeaks && PEAKS.map(([k]) => <td key={k}>{values && values[k] != null ? fmtNum(values[k], 1) : '–'}</td>)}
      <td className="l" colSpan={2} />
    </tr>
  );
  const sq = imported ? (view === 'calc' ? squad && squad.calc : null) : squad;
  return (
    <div className="se-tablewrap">
      <table className="se-table">
        <thead>
          <tr>
            <th className="l">Player</th>
            {!imported && <th className="l" title="in the period">In</th>}
            {!imported && <th className="l" title="group 1 / 2 (split drills)">Grp</th>}
            {!imported && <th className="l" title="modified training: own numbers, left out of squad averages and hint references">Mod</th>}
            <th className="l">Window</th>
            {cols.map(c => head(c.id, c.label, [c.title, c.unit, c.sonraOnly ? (imported ? "Sonra's KPI" : 'not calculated for own periods') : null].filter(Boolean).join(' · ') || undefined))}
            {showPeaks && PEAKS.map(([k, l]) => head(k, l, `highest rolling ${l} in his window (m/min, our calculation); the time under it is where it started`))}
            <th className="l">Flags</th>
            <th className="l">Hints</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => {
            const sp = r.sessionPlayerId;
            const d = draftPlayers ? draftPlayers.get(sp) : null;
            const included = d ? d.included : r.included !== false;
            const noGps = r.flags && r.flags.noGps && !r.error;
            const win = r.window ? [toMs(r.window.start), toMs(r.window.end)] : null;
            const own = win && periodWindow && (win[0] !== periodWindow[0] || win[1] !== periodWindow[1]);
            const hs = hintsBySp.get(sp) || [];
            const acc = (pendingBySp && pendingBySp.get(sp)) || [];
            const f = r.flags || {};
            const peaksOf = imported ? r.calc || {} : r.metrics || {};
            return (
              <tr key={sp} className={`${sp === selectedSp ? 'se-sel' : ''} ${included ? '' : 'se-out'}`} onClick={() => onSelect(sp)}>
                <td className="l" style={{ whiteSpace: 'normal', minWidth: 130 }}>
                  {hs.length > 0 && <span style={{ color: 'var(--se-warn)' }}>⚠ </span>}{labelOf(sp)}
                </td>
                {!imported && (
                  <td className="l" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!included} disabled={readOnly} onChange={() => onToggleInclude(sp)} aria-label={`${labelOf(sp)} in the period`} />
                  </td>
                )}
                {!imported && (
                  <td className="l" onClick={e => e.stopPropagation()}>
                    <select className="se-input" value={d && d.groupNo ? String(d.groupNo) : ''} disabled={readOnly} onChange={e => onSetGroup(sp, e.target.value ? Number(e.target.value) : null)} aria-label="Group">
                      <option value="">–</option><option value="1">1</option><option value="2">2</option>
                    </select>
                  </td>
                )}
                {!imported && (
                  <td className="l" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!(d && d.modified)} disabled={readOnly} onChange={() => onToggleModified(sp)} aria-label="Modified training" />
                  </td>
                )}
                <td className="l se-win">
                  {own ? (
                    <>
                      <span style={{ color: 'var(--se-override)', whiteSpace: 'nowrap' }}>own {clock(win[0])}–{clock(win[1])}</span>
                      {!readOnly && d && (d.start != null || d.end != null) && (
                        <button className="se-btn se-btn--small" style={{ marginLeft: 6, whiteSpace: 'nowrap' }} onClick={e => { e.stopPropagation(); onFollow(sp); }}>Follow period</button>
                      )}
                      {d && d.windowSource && <span className="se-win-note">{d.windowSource}{d.windowNote ? `: ${d.windowNote}` : ''}</span>}
                    </>
                  ) : <span className="se-muted">period</span>}
                </td>
                {r.error
                  ? <td colSpan={cols.length + (showPeaks ? PEAKS.length : 0)} className="l"><span className="se-pill se-pill--warn" title="this drill could not be calculated; the other rows are not affected and it is not in the squad numbers">could not be calculated</span></td>
                  : noGps
                  ? <td colSpan={cols.length + (showPeaks ? PEAKS.length : 0)} className="l"><span className="se-pill" title="no GPS unit that day: nothing to calculate, not part of squad numbers">no GPS</span></td>
                  : cols.map(c => <Cell key={c.id} col={c} row={r} imported={imported} view={view} />)}
                {!noGps && !r.error && showPeaks && PEAKS.map(([k]) => {
                  const p = peaksOf[k];
                  if (!p) return <td key={k}>–</td>;
                  const marked = peakMark && peakMark.sp === sp && peakMark.key === k;
                  return (
                    <td key={k} style={marked ? { outline: '2px solid var(--se-accent)' } : undefined}>
                      {fmtNum(p.value, 1)}
                      <span className="se-at" onClick={e => { e.stopPropagation(); onPeak(sp, k); }} title="show where it happened">{clock(toMs(p.at))}</span>
                    </td>
                  );
                })}
                <td className="l" style={{ whiteSpace: 'normal' }}>
                  {noGps || r.error ? null : (
                    <>
                      {f.missingData && <span className="se-pill se-pill--warn" title={`${Math.round(f.zeroPosShare * 100)} % of samples without position`}>no position {Math.round(f.zeroPosShare * 100)} %</span>}{' '}
                      {f.partial && f.samples > 0 && <span className="se-pill se-pill--warn" title="fewer samples than the window should have (gaps)">partial {Math.round(f.coverage * 100)} %</span>}{' '}
                      {f.samples === 0 && <span className="se-pill">no data in window</span>}{' '}
                      {f.positionQuality && f.positionQuality.poor && <span className="se-pill se-pill--warn" title={f.positionQuality.reasons.join(', ')}>poor position quality</span>}{' '}
                      {d && d.modified && <span className="se-pill">modified</span>}{' '}
                      {(r.warnings || []).filter(w => !/poor position/.test(w)).map(w => <span key={w} className="se-pill se-pill--warn" title={w}>{/differs/.test(w) ? `calc ${r.diffPct > 0 ? '+' : ''}${r.diffPct.toFixed(1)} % vs Sonra` : w}</span>)}
                    </>
                  )}
                </td>
                <td className="l" style={{ whiteSpace: 'normal', minWidth: 160 }} onClick={e => e.stopPropagation()}>
                  {acc.map((d, i) => (
                    <span className="se-hint se-hint--pending" key={`acc-${i}`} title="accepted: part of your draft, written when you press Save (Cancel discards it)">
                      ✓ accepted, not saved yet: {d.reason || d.signal.replace(/_/g, ' ')}
                      {d.maxBefore != null && d.maxAfter != null && Math.abs(d.maxBefore - d.maxAfter) >= 0.05 && <> · max {fmtNum(d.maxBefore, 1)} → {fmtNum(d.maxAfter, 1)} km/h</>}
                    </span>
                  ))}
                  {hs.map(h => (
                    <span className="se-hint" key={`${h.signal}-${h.at || ''}`}>
                      {h.reason}
                      {h.action === 'count_stretch' && (
                        <button disabled={!canCount} title="count this stretch for him (saved straight away; remove it under the speed trace)" onClick={() => onAccept(h)}>✓</button>
                      )}
                      {h.action !== 'none' && h.action !== 'count_stretch' && (
                        <button disabled={readOnly} title={readOnly ? (imported ? 'copy to my periods to apply' : 'read-only') : 'accept (part of the draft until Save)'} onClick={() => onAccept(h)}>✓</button>
                      )}
                      <button disabled={readOnly} title={imported ? 'copy to my periods to dismiss' : readOnly ? 'read-only' : 'dismiss (saved straight away)'} onClick={() => onDismiss(h)}>✕</button>
                    </span>
                  ))}
                </td>
              </tr>
            );
          })}
          {(noGpsLineup || []).map(p => (
            <tr key={`nogps-${p.playerId}`} className="se-out" style={{ opacity: 1 }}>
              <td className="l">{labelOfPlayer(p.playerId)}</td>
              {!imported && <td className="l" colSpan={3}><span className="se-muted">{p.role === 'starter' ? 'starter' : 'sub'}</span></td>}
              <td className="l"><span className="se-muted">{p.start || p.end ? `on ${p.start ? clock(toMs(p.start)) : 'start'}–${p.end ? clock(toMs(p.end)) : 'end'}` : 'whole half'}</span></td>
              <td colSpan={cols.length + (showPeaks ? PEAKS.length : 0)} className="l"><span className="se-pill" title="in the lineup, no GPS unit that day: not part of squad numbers">no GPS</span></td>
              <td className="l" colSpan={2} />
            </tr>
          ))}
          {!imported && squad && squadRow(`Squad mean (${squad.n} with data${squad.modified ? `, ${squad.modified} modified left out` : ''})`, squad.mean, 'our calculation', squad.estimates && squad.estimates.mean)}
          {!imported && squad && squadRow('Squad median', squad.median, 'our calculation', squad.estimates && squad.estimates.median)}
          {imported && view === 'sonra' && squad && squad.sonra && squadRow(`Squad mean, Sonra (${squad.sonra.n})`, squad.sonra.mean, "Sonra's values")}
          {imported && sq && squadRow(`Squad mean, calc (${sq.n} with data)`, sq.mean, 'our calculation', sq.estimates && sq.estimates.mean)}
          {imported && sq && squadRow('Squad median, calc', sq.median, 'our calculation', sq.estimates && sq.estimates.median)}
        </tbody>
      </table>
      <div className="se-muted" style={{ marginTop: 6 }} data-testid="estimate-note">
        HMLD, explosive distance and acceleration/deceleration counts are our own estimates and can differ from Sonra's. SA is acc + dec ≥ 3 m/s², so on own periods it is an estimate too.{' '}
        <details style={{ display: 'inline' }}>
          <summary style={{ display: 'inline', cursor: 'pointer', textDecoration: 'underline' }}>reliability</summary>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            <li>HMLD: about 60 % of drills within 2 % of Sonra; worse in matches (about 69 % within 2 % in match drills, p95 about 9 %). Explosive distance (HMLD − HSR) follows HMLD.</li>
            <li>Acceleration and deceleration counts (≥ 3 m/s²): usually within 1 to 2 efforts; reliable in full match halves (decelerations median error about 4 %); in drills under 10 minutes read them as ±1. SA adds the two, so read it the same way.</li>
            <li>Ranking players within a drill works well (Spearman about 0.92 for accelerations, 0.95 for decelerations): good for squad-level work and comparisons within a drill, not for individual decisions.</li>
            <li>Bias: our counts are about 1 to 2 % lower than Sonra's on average and it depends on the drill (passing drills lower, rondos higher), so a change of drill mix moves a trend.</li>
            <li>Goalkeepers: no deceleration estimate (we count about 16 % too many); accelerations and HMLD are shown.</li>
            <li>Never combine our estimates and Sonra's values in one series or trend. Average metabolic power is our normal calculation (99.7 % of drills within 2 %).</li>
          </ul>
        </details>
      </div>
    </div>
  );
}
